import React, { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Button } from '../ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';

const TOP_NATIONALITIES = ['Indian', 'Filipino', 'Nepali', 'Pakistani'];

const ALL_NATIONALITIES = [
  'Afghan','Albanian','Algerian','American','Andorran','Angolan','Argentine','Armenian',
  'Australian','Austrian','Azerbaijani','Bahamian','Bahraini','Bangladeshi','Barbadian',
  'Belarusian','Belgian','Belizean','Beninese','Bhutanese','Bolivian','Bosnian','Brazilian',
  'British','Bruneian','Bulgarian','Burkinabe','Burundian','Cambodian','Cameroonian',
  'Canadian','Cape Verdean','Central African','Chadian','Chilean','Chinese','Colombian',
  'Comorian','Congolese','Costa Rican','Croatian','Cuban','Cypriot','Czech','Danish',
  'Djiboutian','Dominican','Dutch','East Timorese','Ecuadorean','Egyptian','Emirati',
  'Equatorial Guinean','Eritrean','Estonian','Ethiopian','Fijian','Filipino','Finnish',
  'French','Gabonese','Gambian','Georgian','German','Ghanaian','Greek','Grenadian',
  'Guatemalan','Guinean','Guyanese','Haitian','Honduran','Hungarian','Icelandic','Indian',
  'Indonesian','Iranian','Iraqi','Irish','Israeli','Italian','Ivorian','Jamaican','Japanese',
  'Jordanian','Kazakh','Kenyan','Kuwaiti','Kyrgyz','Laotian','Latvian','Lebanese','Liberian',
  'Libyan','Lithuanian','Luxembourgish','Macedonian','Malagasy','Malawian','Malaysian',
  'Maldivian','Malian','Maltese','Mauritanian','Mauritian','Mexican','Moldovan','Mongolian',
  'Montenegrin','Moroccan','Mozambican','Myanmar','Namibian','Nepali','New Zealander','Nicaraguan',
  'Nigerian','Nigerien','North Korean','Norwegian','Omani','Pakistani','Palestinian',
  'Panamanian','Papua New Guinean','Paraguayan','Peruvian','Polish','Portuguese','Qatari',
  'Romanian','Russian','Rwandan','Saint Lucian','Salvadoran','Samoan','Saudi','Senegalese',
  'Serbian','Sierra Leonean','Singaporean','Slovak','Slovenian','Somali','South African',
  'South Korean','South Sudanese','Spanish','Sri Lankan','Sudanese','Surinamese','Swedish',
  'Swiss','Syrian','Taiwanese','Tajik','Tanzanian','Thai','Togolese','Trinidadian','Tunisian',
  'Turkish','Turkmen','Ugandan','Ukrainian','Uruguayan','Uzbek','Venezuelan','Vietnamese',
  'Yemeni','Zambian','Zimbabwean'
];

const PersonalInfoSection = ({ formData, handleChange }) => {
  const [nationalityOpen, setNationalityOpen] = useState(false);
  const [sameAsPhone, setSameAsPhone] = useState(
    () => !!formData.whatsapp_no && formData.whatsapp_no === formData.phone
  );

  const handleSameAsPhone = (checked) => {
    setSameAsPhone(checked);
    if (checked) handleChange('whatsapp_no', formData.phone || '');
  };

  const otherNationalities = useMemo(() => 
    ALL_NATIONALITIES.filter(n => !TOP_NATIONALITIES.includes(n)),
    []
  );

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Full Name *</Label>
          <Input 
            required 
            value={formData.name} 
            onChange={(e) => handleChange('name', e.target.value)} 
            data-testid="employee-name-input"
          />
        </div>
        <div>
          <Label>Email *</Label>
          <Input 
            type="email" 
            required 
            value={formData.email} 
            onChange={(e) => handleChange('email', e.target.value)} 
            data-testid="employee-email-input"
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input 
            required 
            value={formData.phone} 
            onChange={(e) => {
              handleChange('phone', e.target.value);
              if (sameAsPhone) handleChange('whatsapp_no', e.target.value);
            }}
            data-testid="employee-phone-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>WhatsApp No</Label>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-gray-300 accent-green-600"
                checked={sameAsPhone}
                onChange={(e) => handleSameAsPhone(e.target.checked)}
                data-testid="whatsapp-same-as-phone"
              />
              Same as phone
            </label>
          </div>
          <Input
            value={formData.whatsapp_no || ''}
            onChange={(e) => { setSameAsPhone(false); handleChange('whatsapp_no', e.target.value); }}
            placeholder="e.g. +971501234567 (with country code)"
            data-testid="employee-whatsapp-input"
          />
        </div>
        <div>
          <Label>Date of Birth *</Label>
          <Input 
            type="date" 
            required 
            value={formData.date_of_birth} 
            onChange={(e) => handleChange('date_of_birth', e.target.value)} 
            data-testid="employee-dob-input"
          />
        </div>
        <div>
          <Label>Nationality *</Label>
          <Popover open={nationalityOpen} onOpenChange={setNationalityOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={nationalityOpen}
                className="w-full justify-between font-normal"
                data-testid="employee-nationality-input"
              >
                {formData.nationality || "Select nationality..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search nationality..." />
                <CommandList>
                  <CommandEmpty>No nationality found.</CommandEmpty>
                  <CommandGroup heading="Frequently Used">
                    {TOP_NATIONALITIES.map((nat) => (
                      <CommandItem
                        key={`top-${nat}`}
                        value={nat}
                        onSelect={() => {
                          handleChange('nationality', nat);
                          setNationalityOpen(false);
                        }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${formData.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                        {nat}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandGroup heading="All Nationalities">
                    {otherNationalities.map((nat) => (
                      <CommandItem
                        key={nat}
                        value={nat}
                        onSelect={() => {
                          handleChange('nationality', nat);
                          setNationalityOpen(false);
                        }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${formData.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                        {nat}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label>Gender *</Label>
          <Select value={formData.gender} onValueChange={(value) => handleChange('gender', value)}>
            <SelectTrigger data-testid="employee-gender-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Personal ID</Label>
          <Input 
            value={formData.personal_id} 
            onChange={(e) => handleChange('personal_id', e.target.value)} 
            data-testid="employee-personal-id-input"
          />
        </div>
        <div>
          <Label>Religion</Label>
          <Select 
            value={formData.religion || ''} 
            onValueChange={(v) => handleChange('religion', v)}
          >
            <SelectTrigger data-testid="employee-religion-select">
              <SelectValue placeholder="Select religion" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Hindu">Hindu</SelectItem>
              <SelectItem value="Christianity">Christianity</SelectItem>
              <SelectItem value="Islam">Islam</SelectItem>
              <SelectItem value="Buddhism">Buddhism</SelectItem>
              <SelectItem value="Judaism">Judaism</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Faith / Denomination</Label>
          <Input 
            value={formData.faith || ''} 
            onChange={(e) => handleChange('faith', e.target.value)} 
            placeholder="e.g. Sunni / Catholic"
            data-testid="employee-faith-input"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;
